import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon, SearchXIcon, XIcon, FilmIcon } from 'lucide-react';
import { ProductionCard } from '../components/ProductionCard';
import { EmptyState } from '../components/EmptyState';
import { useCatalog } from '../contexts/CatalogContext';
import { CATEGORY_LABELS, normalizeForSearch } from '../utils/production';
import { buttonClass, pagePadding } from '../utils/styles';
import type { Category } from '../types/production';
export function Search() {
  const [params, setParams] = useSearchParams();
  const {
    published
  } = useCatalog();
  const query = params.get('q') ?? '';
  const category = params.get('categoria') as Category | null ?? null;
  const categories = useMemo(() => Array.from(new Set(published.map(p => p.category).filter((c): c is Category => !!c))), [published]);
  const showFilters = categories.length >= 2;
  const results = useMemo(() => {
    const q = normalizeForSearch(query);
    return published.filter(p => (!q || normalizeForSearch(p.title).includes(q)) && (!category || p.category === category));
  }, [published, query, category]);
  const update = (key: 'q' | 'categoria', value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const chip = (active: boolean) => `h-9 whitespace-nowrap rounded-full border px-4 text-sm font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${active ? 'border-white bg-white text-night' : 'border-graphite text-mist hover:border-white/40 hover:text-white'}`;
  return <div className={`pb-24 pt-28 md:pt-32 ${pagePadding}`} data-id="element-451" data-mp="3vwuqh60nbzc">
      <h1 className="sr-only" data-id="element-452" data-mp="1bb9w9tj326nm">Buscar en NICOLE</h1>
      <div className="relative max-w-3xl" data-id="element-453" data-mp="1fd4v8rdjk7y8">
        <label htmlFor="search-input" className="sr-only" data-id="element-454" data-mp="1fc3i8hcytbdv">
          Buscar por título
        </label>
        <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 h-6 w-6 -translate-y-1/2 text-mist md:h-7 md:w-7" data-id="element-455" data-mp="3cchiblcw7g1i" />
        <input id="search-input" type="search" autoFocus value={query} onChange={e => update('q', e.target.value || null)} placeholder="Buscar por título" className="w-full border-b border-graphite bg-transparent py-4 pl-10 pr-10 font-display text-2xl font-bold text-white placeholder:text-mist/50 transition-colors duration-150 ease-out focus:border-white focus:outline-none md:pl-12 md:text-4xl [&::-webkit-search-cancel-button]:hidden" data-id="element-456" data-mp="3leqvkizjpnil" />
        {query && <button type="button" onClick={() => update('q', null)} aria-label="Borrar búsqueda" className="absolute right-0 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-mist hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-457" data-mp="2xocyge23syh5">
            <XIcon className="h-5 w-5" data-id="element-458" data-mp="pa6rvzt01lrx" />
          </button>}
      </div>

      {showFilters && <div role="group" aria-label="Filtrar por categoría" className="scrollbar-none -mx-5 mt-6 flex gap-2 overflow-x-auto px-5 md:mx-0 md:px-0" data-id="element-459" data-mp="bit9ip6tutyh">
          <button type="button" aria-pressed={!category} onClick={() => update('categoria', null)} className={chip(!category)} data-id="element-460" data-mp="gegx505nvf1v">
            Todas
          </button>
          {categories.map(c => <button key={c} type="button" aria-pressed={category === c} onClick={() => update('categoria', c)} className={chip(category === c)} data-id="element-461" data-mp="u88a6iilph2c">
              {CATEGORY_LABELS[c]}
            </button>)}
        </div>}

      <p className="mt-8 text-sm text-mist" aria-live="polite" data-id="element-462" data-mp="3ey7dgeopb1ym">
        {published.length > 0 && `${results.length} ${results.length === 1 ? 'resultado' : 'resultados'}`}
      </p>

      {published.length === 0 ? <EmptyState icon={FilmIcon} title="Aún no hay producciones publicadas" description="Cuando Nicole publique sus obras podrás buscarlas aquí por título." data-id="element-463" data-mp="32m5ettq3gh4" /> : results.length === 0 ? <EmptyState icon={SearchXIcon} title={query ? `Sin resultados para «${query}»` : 'Sin resultados'} description="Prueba con otro título o elimina los filtros." action={<button type="button" onClick={() => setParams(new URLSearchParams(), {
      replace: true
    })} className={buttonClass('secondary', 'md')} data-id="element-465" data-mp="lzvqsgilz410">
              Limpiar búsqueda
            </button>} data-id="element-464" data-mp="3qfohyfoayjw8" /> : <ul className="mt-6 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-466" data-mp="80w3pdaqux6y">
          {results.map(p => <li key={p.id} data-id="element-467" data-mp="2xr93bu9323lf">
              <ProductionCard production={p} data-id="element-468" data-mp="2xrixzv52oyxg" />
            </li>)}
        </ul>}
    </div>;
}